import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles/ConsumptionMeteringPointGroup.module.scss';
import stylesTable from './styles/ConsumptionMeteringPointGroup.module.scss';
import stylesAccordion from './styles/ConsumptionMeteringPointGroupAccordion.module.scss';

import { useWindowSize } from '../../Layout/Header';

import { OmaLummeApp } from '../../../omaLummeContext';

import Button from '../../Common/Button';
import Dialog from '../../Common/Dialog';
import Loading from '../../Common/Loading';
import Select from '../../Common/Select';
import ReportingGraph from '../../Graphs/ReportingGraph/ReportingGraph';
import ConsumptionMeteringPointGroupNew from './ConsumptionMeteringPointGroupNew';
import ConsumptionMeteringPointGroupEdit from './ConsumptionMeteringPointGroupEdit';
import ConsumptionMeteringPointGroupAccordion from './ConsumptionMeteringPointGroupAccordion';

// Actions.
import {
  fetchMeteringPointGroups,
  fetchMeteringPointGroup,
  deleteMeteringPointGroupConsumptionReport,
  fetchMeteringPointGroupConsumptionReportUrl,
  fetchMeteringPointGroupConsumptionReports,
} from '../../../actions';
// Types
import { MeteringPointGroup, MeteringPointGroupConsumptionReport } from '../../../types/consumptionRates';
import { formatDate, formatFullDateTime } from '../../../utils/billing';

const ConsumptionMeteringPointGroup: React.FunctionComponent = () => {
  const { users } = OmaLummeApp.useState();
  const { userId } = users.activeUser;
  const { t } = useTranslation();

  const [loading, setLoading] = React.useState<boolean>(false);
  const [refreshData, setRefreshData] = React.useState<string>('groups');
  const [groups, setGroups] = React.useState<MeteringPointGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = React.useState<MeteringPointGroup | undefined>(undefined);
  const [activeOption, setActiveOption] = React.useState<string>('');
  const [showCreateModal, setShowCreateModal] = React.useState<boolean>(false);
  const [showEditModal, setShowEditModal] = React.useState<MeteringPointGroup | undefined>(undefined);
  const [groupReports, setGroupReports] = React.useState<MeteringPointGroupConsumptionReport[]>([]);

  const getGroup = React.useCallback(
    (meteringPointId: string) => {
      async function fetchGroup(meteringPointGroupId: string): Promise<void> {
        setLoading(true);
        const group = await fetchMeteringPointGroup(userId, meteringPointGroupId);
        setSelectedGroup(group);
        setLoading(false);
      }
      if ((selectedGroup === undefined && meteringPointId !== '') || (selectedGroup?.identifier !== meteringPointId && meteringPointId !== '')) {
        fetchGroup(meteringPointId);
      }
    },
    [activeOption],
  );

  React.useEffect(() => {
    let unMounted = false;
    async function getGroups(): Promise<void> {
      setLoading(true);
      const groups = await fetchMeteringPointGroups(userId);

      if (!unMounted && groups.length > 0) {
        setGroups(groups.sort((a, b) => (a.groupName > b.groupName ? 1 : -1)));
        setActiveOption(groups[0].identifier);
        setSelectedGroup(undefined);

        getGroup(activeOption);
      }
      setLoading(false);
      setRefreshData('reports');
    }

    if (refreshData === 'groups') {
      getGroups();
    }

    return (): void => {
      unMounted = true;
    };
  }, [userId, refreshData]);

  React.useEffect(() => {
    if (selectedGroup?.identifier !== activeOption) {
      getGroup(activeOption);
    }
  }, [activeOption]);

  const generateGroupOptions = (groups: MeteringPointGroup[]): { value: string; label: string }[] => {
    const options: { value: string; label: string }[] = [];

    groups.forEach((g) => {
      options.push({
        value: g.identifier,
        label: g.groupName,
      });
    });
    return options;
  };

  React.useEffect(() => {
    let unMounted = false;
    async function getReports(): Promise<void> {
      if (selectedGroup?.identifier && !unMounted) {
        const reports = await fetchMeteringPointGroupConsumptionReports(userId, selectedGroup?.identifier);

        if (reports && reports.length > 0) {
          setGroupReports(reports.sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1)));
          setRefreshData('');
          if (reports.find((r) => r.status.toLowerCase() === 'processing')) {
            await new Promise((resolve) => setTimeout(resolve, 5000));

            setRefreshData('reports');
          }
        } else {
          setGroupReports([]);
          setRefreshData('');
        }
      }
    }

    if (
      refreshData === 'reports' ||
      groupReports.filter((report) => report.meteringPointGroupIdentifier === selectedGroup?.identifier).length === 0
    ) {
      getReports();
    }

    return (): void => {
      unMounted = true;
    };
  }, [selectedGroup, refreshData]);

  async function downloadReport(meteringPointGroupId: string, identifier: string): Promise<void> {
    let fileUrl: string | undefined = undefined;

    fileUrl = await fetchMeteringPointGroupConsumptionReportUrl(userId, meteringPointGroupId, identifier);

    if (fileUrl) {
      const invisibleExcelLink = document.createElement('a');
      document.body.appendChild(invisibleExcelLink);
      invisibleExcelLink.href = fileUrl;
      invisibleExcelLink.click();
    }
  }

  async function deleteReport(meteringPointGroupId: string, identifier: string): Promise<void> {
    await deleteMeteringPointGroupConsumptionReport(userId, meteringPointGroupId, identifier);

    setRefreshData('reports');
  }

  const windowWidth = useWindowSize()[0];

  const isMobile = windowWidth < 768;

  return (
    <>
      <p className={styles.ingress}>{t(`consumption:meteringPointGroup.groupsIngress`)}</p>

      {!isMobile && (
        <>
          <header className={styles.groupHeader}>
            {!loading && groups.length === 0 && <p className={styles.noGroups}>{t(`consumption:meteringPointGroup.noGroups`)}</p>}
            {groups.length > 0 && (
              <Select
                options={generateGroupOptions(groups)}
                activeOption={activeOption}
                setActiveOption={setActiveOption}
                search={t(`consumption:meteringPointGroup.searchGroup`)}
              />
            )}

            <div className={styles.buttons}>
              <Button type="button" onClick={(): void => setShowCreateModal(true)}>
                {t(`consumption:meteringPointGroup.createNewGroup`)}
              </Button>
            </div>
          </header>

          <Dialog openModal={showCreateModal} closeModal={() => setShowCreateModal(false)}>
            <ConsumptionMeteringPointGroupNew setShowCreateModal={setShowCreateModal} setRefreshData={setRefreshData} />
          </Dialog>
          <Dialog openModal={showEditModal ? true : false} closeModal={() => setShowEditModal(showEditModal)}>
            {showEditModal ? (
              <ConsumptionMeteringPointGroupEdit group={showEditModal} setShowEditModal={setShowEditModal} setRefreshData={setRefreshData} />
            ) : (
              <></>
            )}
          </Dialog>

          {!loading && selectedGroup && (
            <ConsumptionMeteringPointGroupAccordion
              type="read"
              group={selectedGroup}
              setShowEditModal={setShowEditModal}
              setRefreshData={setRefreshData}
            />
          )}

          {loading && <Loading />}
          {selectedGroup && selectedGroup.meteringPoints.length === 0 && <p>{t(`consumption:meteringPointGroup.noMeteringPoints`)}</p>}
          {selectedGroup && selectedGroup.meteringPoints.length > 0 && (
            <ReportingGraph type="group" meteringPointGroup={selectedGroup.identifier} refresh={setRefreshData} />
          )}

          {groupReports.length > 0 && (
            <>
              <h2>{t(`consumption:meteringPointGroup.reportsTitle`)}</h2>
              <div className={`table-responsive ${stylesTable.tableWrapper} ${stylesAccordion.accordionTable}`}>
                <table className={stylesTable.table}>
                  <thead>
                    <tr>
                      <th>{t(`consumption:meteringPointGroup.reportsCreatedAt`)}</th>
                      <th>{t(`consumption:meteringPointGroup.reportsPeriod`)}</th>
                      <th>{t(`consumption:meteringPointGroup.reportsResolution`)}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupReports.map((report) => (
                      <tr key={report.identifier}>
                        <td>{formatFullDateTime(Date.parse(report.createdAt))}</td>
                        <td>
                          {formatDate(report.periodStart)} – {formatDate(report.periodEnd)}
                        </td>
                        <td>{t(`consumption:meteringPointGroup.reports-${report.period.toLowerCase()}`)}</td>
                        <td>
                          {report.status.toLowerCase() === 'created' && (
                            <button
                              className={styles.download}
                              onClick={(e): void => {
                                e.preventDefault();
                                downloadReport(report.meteringPointGroupIdentifier, report.identifier);
                              }}
                              title={t(`common:general.downloadButton`)}
                            >
                              {t(`common:general.downloadButton`)}
                            </button>
                          )}
                          {report.status.toLowerCase() === 'processing' && <Loading size="smallLeft" />}
                          {report.status.toLowerCase() === 'failed' && report.status}
                          {report.status.toLowerCase() !== 'processing' && (
                            <button
                              className={styles.delete}
                              onClick={(e): void => {
                                e.preventDefault();
                                deleteReport(report.meteringPointGroupIdentifier, report.identifier);
                              }}
                              title={t(`common:general.deleteButton`)}
                            >
                              {t(`common:general.deleteButton`)}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

export default ConsumptionMeteringPointGroup;
